import React, { useState, useEffect } from 'react';
import Layout from '../../Layout/Layout';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { 
  FaSave, FaSpinner, FaExclamationTriangle,
  FaMoneyBillWave, FaCreditCard, FaMobileAlt,
  FaUniversity, FaWallet, FaInfoCircle,
  FaCheckCircle, FaTimesCircle, FaChevronDown
} from 'react-icons/fa';
import './CreatePaymentMethod.css';

// API Configuration
const API_BASE_URL = 'http://localhost:8000/api';

// SweetAlert2 Configuration
const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true
});

const CreatePaymentMethod = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    notes: '',
    payment_type: 'bank_transfer',
    balance: 0
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [tokenExpired, setTokenExpired] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  const paymentTypes = [
    { 
      value: 'bank_transfer', 
      label: 'Bank Transfer',
      icon: <FaUniversity />,
      description: 'Traditional bank account or transfer'
    },
    { 
      value: 'mobile_payment', 
      label: 'Mobile Payment',
      icon: <FaMobileAlt />,
      description: 'Mobile money or digital wallet'
    },
    { 
      value: 'cash', 
      label: 'Cash',
      icon: <FaMoneyBillWave />,
      description: 'Physical cash or money on hand'
    }
  ];

  // Load user data on component mount
  useEffect(() => {
    const loadUserData = () => {
      try {
        const storedUserData = localStorage.getItem('userData');
        if (storedUserData) {
          const parsedUserData = JSON.parse(storedUserData);
          setUserData(parsedUserData);
          console.log('Loaded user data:', parsedUserData);
        } 
      } catch (error) {
        console.error('Error loading user data:', error);
        setTokenExpired(true);
        showErrorAlert('Error loading user data. Please log in again.');
      }
    };

    loadUserData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when field is edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // Clear API error when form is edited
    if (apiError) {
      setApiError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.payment_type) {
      newErrors.payment_type = 'Payment type is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const showSuccessAlert = (message) => {
    Toast.fire({
      icon: 'success',
      title: message,
      iconHtml: '<i class="fas fa-check-circle"></i>',
      customClass: {
        popup: 'swal2-popup-custom',
        title: 'swal2-title-custom'
      }
    });
  };

  const showErrorAlert = (message) => {
    Toast.fire({
      icon: 'error',
      title: message,
      iconHtml: '<i class="fas fa-times-circle"></i>',
      customClass: {
        popup: 'swal2-popup-custom',
        title: 'swal2-title-custom'
      }
    });
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;


    setIsSubmitting(true);
    setApiError('');

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token not found. Please log in.');
      }

      const payload = {
        name: formData.name.trim(),
        notes: formData.notes.trim(),
        payment_type: formData.payment_type,
        balance: 0,
      };

      console.log('Submitting payment method data:', payload);

      const response = await fetch(`${API_BASE_URL}/payment-methods/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.status === 401) {
        throw new Error('Session expired');
      }

      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(
          responseData.message || 
          responseData.detail || 
          responseData.error || 
          'Failed to create payment method'
        );
      }

      console.log('Payment method created successfully:', responseData);
      
      // Show success message
      showSuccessAlert('Payment method created successfully!');
      
      // Navigate after a short delay
      setTimeout(() => {
        navigate('/payment-methods');
      }, 1500);

    } catch (error) {
      console.error('Error creating payment method:', error);
      
      if (error.message.includes('400')) {
        showErrorAlert('Invalid data provided. Please check your input and try again.');
      } else if (error.message.includes('403')) {
        showErrorAlert('You do not have permission to create payment methods.');
      } else if (error.message.includes('404')) {
        showErrorAlert('The payment method endpoint was not found.');
      } else if (error.message.includes('500')) {
        showErrorAlert('Server error. Please try again later.');
      } else {
        showErrorAlert(error.message || 'An error occurred while creating the payment method');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({
      ...prev,
      payment_type: value
    }));
    setIsSelectOpen(false);
    if (errors.payment_type) {
      setErrors(prev => ({
        ...prev,
        payment_type: ''
      }));
    }
  };

  const selectedPaymentType = paymentTypes.find(type => type.value === formData.payment_type);

  
  return (
    <Layout>
      <div className="create-payment-method">
        <div className="page-header">
          <h1>Create Payment Method</h1>
          <p>Add a new payment method to track your transactions and manage your finances effectively</p>
        </div>

        <form onSubmit={handleSubmit} className="payment-method-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="name">
                <FaWallet /> Payment Method Name
              </label>
              <input
                style={{width: 'auto'}}
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Main Bank Account, Mobile Wallet"
                className={errors.name ? 'error' : ''}
              />
              {errors.name && (
                <span className="error-message">
                  <FaTimesCircle /> {errors.name}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="payment_type">
                <FaCreditCard /> Payment Type
              </label>
              <div className="select-container">
                <div 
                  className={`select-field ${isSelectOpen ? 'open' : ''} ${errors.payment_type ? 'error' : ''}`}
                  onClick={() => setIsSelectOpen(!isSelectOpen)}
                >
                  <div className="select-selected">
                    {selectedPaymentType && (
                      <>
                        <div className="option-icon">{selectedPaymentType.icon}</div>
                        <div className="option-details">
                          <span className="option-label">{selectedPaymentType.label}</span>
                        </div>
                      </>
                    )}
                  </div>
                  <FaChevronDown className={`select-arrow ${isSelectOpen ? 'open' : ''}`} />
                </div>
                
                {isSelectOpen && (
                  <div className="select-options">
                    {paymentTypes.map(type => (
                      <div
                        key={type.value}
                        className={`select-option ${formData.payment_type === type.value ? 'selected' : ''}`}
                        onClick={() => handleSelectChange(type.value)}
                      >
                        <div className="option-icon">{type.icon}</div>
                        <div className="option-details">
                          <span className="option-label">{type.label}</span>
                          <span className="option-description">{type.description}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {errors.payment_type && (
                <span className="error-message">
                  <FaTimesCircle /> {errors.payment_type}
                </span>
              )}
            </div>

            <div className="form-group full-width">
              <label htmlFor="notes">
                <FaInfoCircle /> Notes
              </label>
              <textarea
                style={{width: 'auto'}}
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Add any additional details about this payment method (optional)..."
                rows="3"
              />
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={() => navigate('/payment-methods')}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="spinner" /> Creating...
                </>
              ) : (
                <>
                  <FaSave /> Create Payment Method
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default CreatePaymentMethod;
